import { css } from '@emotion/css';
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Center,
  Divider,
  Group,
  List,
  Pagination,
  Space,
  Table,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { upperFirst, useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconArrowLeft,
  IconAt,
  IconCalendarMonth,
  IconEdit,
  IconReport,
  IconRotate,
  IconUser,
} from '@tabler/icons-react';
import { DeleteButton } from 'components/DeleteButton';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { chunk, isEmpty, partition } from 'remeda';
import { useArchiveReport } from 'shared/hooks/use-archive-report';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';
import { useLoadReport } from 'shared/hooks/use-load-report';

import {
  EditReportNameModal,
  EditReportRecipientsModal,
  EditReportRecurrenceModal,
  ReportExportDownloadButton,
} from './components';

const DAY_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const styles = {
  listIconFix: css`
    .mantine-List-itemWrapper {
      align-items: normal;
    }
  `,
};

export const Report = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useMantineTheme();
  const { reportId } = useParams<{ reportId: string }>();
  const { report, isReportLoading } = useLoadReport(reportId);
  const { users = [] } = useLoadLightUsers();
  const { archiveReport, isArchiveReportLoading } = useArchiveReport();
  const [isEmailListUnfolded, setIsEmailListUnfolded] = useState(false);
  const [page, setPage] = useState(1);
  const [isEditNameModalOpen, editNameModalHandlers] = useDisclosure(false);
  const [isEditRecurrenceModalOpen, editRecurrenceModalHandlers] = useDisclosure(false);
  const [isEditRecipientsModalOpen, editRecipientsModalHandlers] = useDisclosure(false);

  if (isReportLoading) {
    return <div>Loading...</div>;
  }

  if (report == null) {
    console.error('Report empty even though loading is done');
    return null;
  }

  const handleDelete = () => {
    modals.openConfirmModal({
      centered: true,
      title: t(i18nKeys.REPORTS.SHOW.DELETE_REPORT),
      children: <Text>{t(i18nKeys.REPORTS.SHOW.AYS_DELETE)}</Text>,
      labels: { cancel: t(i18nKeys.CANCEL), confirm: t(i18nKeys.DELETE) },
      confirmProps: { color: 'red', loading: isArchiveReportLoading },
      onConfirm: () => archiveReport(report.id),
      onCancel: modals.closeAll,
    });
  };

  const days =
    report.periodicity.type === 'weekly'
      ? report.periodicity.days
          // @ts-expect-error
          .map(day => t(i18nKeys.DATES.DAY_NAMES[DAY_OF_WEEK[day - 1].toUpperCase()]))
          .join(', ')
      : report.periodicity.days
          .map(day => {
            const fakeDate = dayjs().month(0).date(day);
            return fakeDate.format('Do');
          })
          .join(', ');

  const userEmails = users.map(user => user.email);
  const [internalEmails, externalEmails] = partition(report.emails, email =>
    userEmails.includes(email),
  );
  const sortedEmails = [...internalEmails, ...externalEmails];
  const iconExternalEmail = <IconAt stroke={1.25} size="1rem" color={theme.colors.gray[5]} />;

  const exportsSorted = [...report.exports].reverse();

  const pages = chunk(exportsSorted, 20);

  return (
    <>
      <EditReportNameModal
        isOpen={isEditNameModalOpen}
        onClose={editNameModalHandlers.close}
        report={report}
      />
      <EditReportRecurrenceModal
        isOpen={isEditRecurrenceModalOpen}
        onClose={editRecurrenceModalHandlers.close}
        report={report}
      />
      <EditReportRecipientsModal
        isOpen={isEditRecipientsModalOpen}
        onClose={editRecipientsModalHandlers.close}
        report={report}
      />
      <PageTitle>
        <ActionIcon onClick={() => history.push('/reports')} size="lg" variant="light">
          <IconArrowLeft stroke={1.5} />
        </ActionIcon>
        {report.name}
        <PageTitle.Actions>
          <DeleteButton onClick={handleDelete} />
        </PageTitle.Actions>
      </PageTitle>
      <Panel title={t(i18nKeys.REPORTS.SHOW.DETAILS)} withBorder>
        <Group align="start">
          <Box mt={-5} mr="lg">
            <IconReport size="120" stroke={0.5} color={theme.colors.purple[6]} />
          </Box>
          <Box flex={1}>
            <Group align="top" justify="space-between">
              <Text size="lg" c="dimmed" fw={500} mb="sm">
                {t(i18nKeys.REPORTS.SHOW.NAME)}
              </Text>
              <Button
                onClick={editNameModalHandlers.open}
                size="xs"
                color="purple.4"
                variant="light"
                leftSection={<IconEdit size={16} stroke={1.5} />}
              >
                {t(i18nKeys.REPORTS.INDEX.ACTIONS.EDIT)}
              </Button>
            </Group>
            <Text>{report.name}</Text>
          </Box>
          <Divider orientation="vertical" />
          <Box flex={1.5}>
            <Group align="top" justify="space-between">
              <Text size="lg" c="dimmed" fw={500} mb="sm">
                {t(i18nKeys.REPORTS.SHOW.RECURRENCE)}
              </Text>
              {/* TODO: extract button */}
              <Button
                onClick={editRecurrenceModalHandlers.open}
                size="xs"
                color="purple.4"
                variant="light"
                leftSection={<IconEdit size={16} stroke={1.5} />}
              >
                {t(i18nKeys.REPORTS.INDEX.ACTIONS.EDIT)}
              </Button>
            </Group>
            <Group mb="xs" gap="xs">
              <IconRotate color={theme.colors.gray[5]} stroke={1.7} size="1.25rem" />
              <Text c="gray.8">
                {/* @ts-ignore */}
                {t(i18nKeys.REPORTS.RECURRENCE[report.periodicity.type.toUpperCase()])}
              </Text>
            </Group>
            <Group gap="xs" wrap="nowrap" align="start">
              {/* div required here so the svg doesn't get squished by the text when it waps */}
              <div>
                <IconCalendarMonth
                  color={theme.colors.gray[5]}
                  stroke={1.7}
                  size={20}
                  style={{ marginTop: '2px' }}
                />
              </div>
              <Text c="gray.8" span>
                {days}
              </Text>
            </Group>
          </Box>
          <Divider orientation="vertical" />
          <Box flex={1.5}>
            <Group align="top" justify="space-between">
              <Text size="lg" c="dimmed" fw={500} mb="sm">
                {t(i18nKeys.REPORTS.SHOW.RECIPIENTS)}
              </Text>
              <Button
                onClick={editRecipientsModalHandlers.open}
                size="xs"
                color="purple.4"
                variant="light"
                leftSection={<IconEdit size={16} stroke={1.5} />}
              >
                {t(i18nKeys.REPORTS.INDEX.ACTIONS.EDIT)}
              </Button>
            </Group>
            <List
              className={styles.listIconFix}
              mb={4}
              spacing={6}
              c="gray.8"
              size="sm"
              center
              icon={<IconUser size="1rem" stroke={1.25} color={theme.colors.gray[5]} />}
            >
              {sortedEmails.slice(0, isEmailListUnfolded ? Infinity : 4).map(email => (
                <List.Item
                  key={email}
                  icon={externalEmails.includes(email) ? iconExternalEmail : undefined}
                >
                  {email}
                </List.Item>
              ))}
            </List>
            {sortedEmails.length > 4 && (
              <Anchor onClick={() => setIsEmailListUnfolded(state => !state)} size="sm">
                {isEmailListUnfolded ? 'See less' : `See ${sortedEmails.length - 4} more`}
              </Anchor>
            )}
          </Box>
        </Group>
      </Panel>
      <Space h="lg" />
      <Panel
        title="Exports"
        withBorder
        footer={
          <Pagination
            disabled={pages.length === 1}
            total={pages.length}
            value={page}
            onChange={setPage}
            size="sm"
          />
        }
      >
        {isEmpty(pages) ? (
          <Center h={200}>
            <Text>{t(i18nKeys.REPORTS.SHOW.NO_EXPORTS_YET)}</Text>
          </Center>
        ) : (
          <Table verticalSpacing="xs" striped>
            <Table.Tbody>
              {pages[page - 1].map(({ executionDate, file, xlsxFile }) => (
                <Table.Tr key={executionDate.toString()}>
                  <Table.Td>
                    <Text>{upperFirst(executionDate.format('LLLL'))}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      {xlsxFile != null && (
                        <ReportExportDownloadButton href={xlsxFile} format="xlsx" />
                      )}
                      <ReportExportDownloadButton href={file} format="csv" />
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Panel>
    </>
  );
};
